interface CacheItem {
  address: string;
  expires: number;
  timestamp: number;
  isPrecise?: boolean; // true si l'adresse contient une rue
}

interface CacheData {
  [key: string]: CacheItem;
}

export class GeocodingCache {
  private static CACHE_KEY = 'safee_geocoding_cache';
  private static CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 jours
  private static IMPRECISE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes pour les adresses imprécises
  private static MAX_CACHE_SIZE = 1000;
  private static memoryCache: Map<string, CacheItem> = new Map();

  /**
   * Generate a cache key from coordinates
   * Using 6 decimal places for ~0.1m precision
   */
  private static getCacheKey(lat: number, lng: number): string {
    return `${lat.toFixed(6)},${lng.toFixed(6)}`;
  }

  /**
   * List of invalid/error addresses that should not be cached
   */
  private static INVALID_ADDRESSES = [
    'API key not configured',
    'Adresse inconnue',
    'Erreur de géocodage',
    'Adresse non trouvée',
    '',
  ];

  /**
   * List of major French cities - used to detect imprecise addresses
   */
  private static MAJOR_CITIES = [
    'Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Nantes', 'Montpellier',
    'Strasbourg', 'Bordeaux', 'Lille', 'Rennes', 'Reims', 'Saint-Étienne',
    'Toulon', 'Le Havre', 'Grenoble', 'Dijon', 'Angers', 'Nîmes', 'Villeurbanne'
  ];

  /**
   * Check if an address appears to be imprecise (just a city name without street)
   * Detects patterns like:
   * - "Marseille"
   * - "Marseille, France"
   * - "Marseille (13006)"
   * - "Marseille, 13006"
   * - "13006 Marseille"
   */
  private static isImpreciseAddress(address: string): boolean {
    if (!address || address.length === 0) return true;

    const trimmed = address.trim();

    // Extract city name by removing postal codes and country
    // Remove patterns like "(13006)", "13006", ", France"
    const cleanedForCityCheck = trimmed
      .replace(/\s*\(\d{5}\)/g, '')           // Remove "(13006)"
      .replace(/\s*,\s*\d{5}\s*/g, '')        // Remove ", 13006"
      .replace(/^\d{5}\s+/g, '')              // Remove "13006 " at start
      .replace(/\s*,\s*France\s*$/i, '')      // Remove ", France" at end
      .trim();

    // Check if what remains is just a city name
    const isCityOnly = this.MAJOR_CITIES.some(city =>
      cleanedForCityCheck.toLowerCase() === city.toLowerCase()
    );

    if (isCityOnly) {
      return true;
    }

    // Check if it's just a city name (no comma = no street)
    if (!trimmed.includes(',') && !trimmed.includes('(')) {
      return this.MAJOR_CITIES.some(city =>
        trimmed.toLowerCase() === city.toLowerCase()
      );
    }

    // Check if it's "City, Country" or "City, PostalCode" format (no street)
    const parts = trimmed.split(',').map(p => p.trim());
    if (parts.length === 2) {
      const [first, second] = parts;
      // Clean first part of any postal code in parentheses
      const firstClean = first.replace(/\s*\(\d{5}\)/g, '').trim();

      // If first part is a major city and second is a country or postal code
      if (this.MAJOR_CITIES.some(city => firstClean.toLowerCase() === city.toLowerCase())) {
        if (second.toLowerCase() === 'france' || second.match(/^\d{5}$/)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if an address is valid (not an error message)
   */
  private static isValidAddress(address: string): boolean {
    return !this.INVALID_ADDRESSES.includes(address);
  }

  /**
   * Get address from cache (memory first, then localStorage)
   * Returns null for imprecise addresses that have expired (short TTL)
   */
  static get(lat: number, lng: number): string | null {
    const key = this.getCacheKey(lat, lng);

    // Check memory cache first
    const memoryItem = this.memoryCache.get(key);
    if (memoryItem && Date.now() < memoryItem.expires) {
      // Don't return invalid/error addresses from cache
      if (!this.isValidAddress(memoryItem.address)) {
        this.memoryCache.delete(key);
        return null;
      }
      return memoryItem.address;
    }

    // If memory item expired, delete it
    if (memoryItem) {
      this.memoryCache.delete(key);
    }

    // Check localStorage cache
    try {
      const cache = this.getLocalStorageCache();
      const item = cache[key];

      if (item && Date.now() < item.expires) {
        // Don't return invalid/error addresses from cache
        if (!this.isValidAddress(item.address)) {
          delete cache[key];
          localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
          return null;
        }
        // Add to memory cache for faster access
        this.memoryCache.set(key, item);
        return item.address;
      }

      // Clean up expired item
      if (item) {
        delete cache[key];
        localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
      }
    } catch (error) {
      console.error('Error reading geocoding cache:', error);
    }

    return null;
  }

  /**
   * Set address in cache (both memory and localStorage)
   * Imprecise addresses get a shorter cache duration (5 min instead of 30 days)
   * so they can be refreshed with better results
   */
  static set(lat: number, lng: number, address: string): void {
    // Don't cache invalid/error addresses
    if (!this.isValidAddress(address)) {
      return;
    }

    const key = this.getCacheKey(lat, lng);
    const isPrecise = !this.isImpreciseAddress(address);

    // Use shorter cache duration for imprecise addresses
    const duration = isPrecise ? this.CACHE_DURATION : this.IMPRECISE_CACHE_DURATION;

    const item: CacheItem = {
      address,
      expires: Date.now() + duration,
      timestamp: Date.now(),
      isPrecise
    };

    // Add to memory cache
    this.memoryCache.set(key, item);

    // Add to localStorage cache
    try {
      const cache = this.getLocalStorageCache();
      cache[key] = item;

      // Clean old entries if cache is too large
      this.cleanCache(cache);

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error saving to geocoding cache:', error);
    }
  }

  /**
   * Check if a cached address is imprecise (useful for deciding to refresh)
   */
  static isAddressImprecise(lat: number, lng: number): boolean {
    const key = this.getCacheKey(lat, lng);
    const memoryItem = this.memoryCache.get(key);
    if (memoryItem) {
      return memoryItem.isPrecise === false;
    }
    try {
      const cache = this.getLocalStorageCache();
      const item = cache[key];
      if (item) {
        return item.isPrecise === false;
      }
    } catch (error) {
      // Ignore
    }
    return false;
  }

  /**
   * Force update an address even if cached (useful when we get a better result)
   */
  static forceUpdate(lat: number, lng: number, address: string): void {
    // Always update, bypassing the cache check
    const key = this.getCacheKey(lat, lng);
    const isPrecise = !this.isImpreciseAddress(address);
    const duration = isPrecise ? this.CACHE_DURATION : this.IMPRECISE_CACHE_DURATION;

    const item: CacheItem = {
      address,
      expires: Date.now() + duration,
      timestamp: Date.now(),
      isPrecise
    };

    this.memoryCache.set(key, item);

    try {
      const cache = this.getLocalStorageCache();
      cache[key] = item;
      this.cleanCache(cache);
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error force updating geocoding cache:', error);
    }
  }

  /**
   * Get localStorage cache data
   */
  private static getLocalStorageCache(): CacheData {
    try {
      const cacheStr = localStorage.getItem(this.CACHE_KEY);
      return cacheStr ? JSON.parse(cacheStr) : {};
    } catch (error) {
      console.error('Error parsing geocoding cache:', error);
      return {};
    }
  }

  /**
   * Clean cache by removing expired and oldest entries
   */
  private static cleanCache(cache: CacheData): void {
    const now = Date.now();
    const keys = Object.keys(cache);
    
    // Remove expired entries
    keys.forEach(key => {
      if (cache[key].expires < now) {
        delete cache[key];
      }
    });
    
    // If still too large, remove oldest entries
    const remainingKeys = Object.keys(cache);
    if (remainingKeys.length > this.MAX_CACHE_SIZE) {
      // Sort by timestamp (oldest first)
      remainingKeys.sort((a, b) => cache[a].timestamp - cache[b].timestamp);
      
      // Remove oldest entries
      const toRemove = remainingKeys.length - this.MAX_CACHE_SIZE;
      remainingKeys.slice(0, toRemove).forEach(key => {
        delete cache[key];
        this.memoryCache.delete(key); // Also remove from memory cache
      });
    }
  }

  /**
   * Clear all cache
   */
  static clear(): void {
    this.memoryCache.clear();
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      console.error('Error clearing geocoding cache:', error);
    }
  }

  /**
   * Remove invalid/error entries from cache
   * Call this to clean up cached errors after fixing API issues
   */
  static cleanInvalidEntries(): number {
    let removedCount = 0;

    // Clean memory cache
    for (const [key, item] of this.memoryCache.entries()) {
      if (!this.isValidAddress(item.address)) {
        this.memoryCache.delete(key);
        removedCount++;
      }
    }

    // Clean localStorage cache
    try {
      const cache = this.getLocalStorageCache();
      for (const key of Object.keys(cache)) {
        if (!this.isValidAddress(cache[key].address)) {
          delete cache[key];
          removedCount++;
        }
      }
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error cleaning invalid cache entries:', error);
    }

    return removedCount;
  }

  /**
   * Remove all imprecise address entries from cache
   * This forces re-fetching addresses that only contain city names
   */
  static cleanImpreciseEntries(): number {
    let removedCount = 0;

    // Clean memory cache
    for (const [key, item] of this.memoryCache.entries()) {
      if (item.isPrecise === false || this.isImpreciseAddress(item.address)) {
        this.memoryCache.delete(key);
        removedCount++;
      }
    }

    // Clean localStorage cache
    try {
      const cache = this.getLocalStorageCache();
      for (const key of Object.keys(cache)) {
        const item = cache[key];
        if (item.isPrecise === false || this.isImpreciseAddress(item.address)) {
          delete cache[key];
          removedCount++;
        }
      }
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error cleaning imprecise cache entries:', error);
    }

    return removedCount;
  }

  /**
   * Get cache statistics
   */
  static getStats(): { 
    memoryCacheSize: number; 
    localStorageSize: number; 
    totalHits?: number;
    totalMisses?: number;
  } {
    const localCache = this.getLocalStorageCache();
    return {
      memoryCacheSize: this.memoryCache.size,
      localStorageSize: Object.keys(localCache).length
    };
  }

  /**
   * Check if coordinates are already cached
   */
  static isCached(lat: number, lng: number): boolean {
    return this.get(lat, lng) !== null;
  }
}